import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const AdminSidebar = ({ isMobileOpen, onClose }) => {
  const location = useLocation();
  const [isSmallScreen, setIsSmallScreen] = React.useState(window.innerWidth < 480);

  React.useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    { path: '/admin', icon: '📊', label: 'Dashboard' },
    { path: '/admin/products', icon: '🛍️', label: 'Products' },
    { path: '/admin/categories', icon: '📁', label: 'Categories' },
    { path: '/admin/orders', icon: '📦', label: 'Orders' },
    { path: '/admin/users', icon: '👥', label: 'Users' },
    { path: '/admin/promotions', icon: '🎯', label: 'Promotions' },
    { path: '/admin/profile', icon: '👤', label: 'Profile' },
  ];

  const handleItemClick = () => {
    if (window.innerWidth < 768 && onClose) {
      onClose();
    }
  };

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  return (
    <>
      {/* Overlay for mobile */}
      {isMobileOpen && window.innerWidth < 768 && (
        <div 
          className="sidebar-overlay active" 
          onClick={onClose}
        />
      )}

      <aside 
        className={`admin-sidebar ${isMobileOpen ? 'mobile-open' : ''}`}
      >
        {/* Mobile Close Button */}
        {window.innerWidth < 768 && (
          <button 
            onClick={onClose}
            className="sidebar-close"
            aria-label="Close menu"
          >
            ✕
          </button>
        )}

        {/* Logo Section */}
        <div className="sidebar-header" style={{ 
          padding: isSmallScreen ? 'var(--space-3)' : 'var(--space-4)',
          borderBottom: '1px solid var(--border-light)',
          backgroundColor: 'var(--bg-light)',
          position: 'relative'
        }}>
          <h2 style={{ 
            color: 'var(--text-dark)', 
            fontSize: isSmallScreen ? 'var(--font-size-base)' : 'var(--font-size-lg)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            fontWeight: '700',
            marginBottom: '2px'
          }}>
            <span style={{ color: 'var(--primary)' }}>Bloom</span>
            <span style={{ color: 'var(--accent-gold)' }}>Soko</span>
          </h2>
          <p style={{ 
            color: 'var(--text-light)', 
            fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
            margin: 0
          }}>
            Admin Panel
          </p>
        </div>

        {/* Navigation Menu */}
        <nav style={{ 
          padding: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)',
          paddingBottom: window.innerWidth < 768 ? '60px' : '0'
        }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path} style={{ marginBottom: '2px' }}>
                  <Link
                    to={item.path}
                    className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                    onClick={handleItemClick}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : 'var(--space-3)',
                      color: isActive ? 'var(--accent-gold)' : 'var(--text-medium)',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      borderLeft: `3px solid ${isActive ? 'var(--accent-gold)' : 'transparent'}`,
                      gap: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)',
                      backgroundColor: isActive ? 'rgba(255, 193, 7, 0.1)' : 'transparent',
                      borderRadius: 'var(--radius-sm)',
                      minHeight: isSmallScreen ? '40px' : '48px'
                    }}
                  >
                    <span style={{ 
                      fontSize: isSmallScreen ? '1rem' : '1.1rem',
                      flexShrink: 0
                    }}>
                      {item.icon}
                    </span>
                    <span style={{ 
                      fontWeight: isActive ? '600' : '400',
                      fontSize: isSmallScreen ? 'var(--font-size-sm)' : 'var(--font-size-base)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {item.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Mobile Footer - User Info */}
        {window.innerWidth < 768 && (
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)',
            borderTop: '1px solid var(--border-light)',
            backgroundColor: 'var(--bg-white)',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
            zIndex: 1001,
            width: isMobileOpen ? (isSmallScreen ? '90vw' : '85vw') : '100%',
            maxWidth: isMobileOpen ? '280px' : '100%',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 'var(--space-2)' 
            }}>
              <div style={{
                width: isSmallScreen ? '32px' : '36px',
                height: isSmallScreen ? '32px' : '36px',
                borderRadius: '50%',
                backgroundColor: 'var(--accent-gold)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-dark)',
                fontWeight: '600',
                fontSize: isSmallScreen ? 'var(--font-size-sm)' : 'var(--font-size-base)',
                flexShrink: 0
              }}>
                {adminUser.name?.charAt(0) || 'A'}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ 
                  fontWeight: '600', 
                  color: 'var(--text-dark)',
                  fontSize: isSmallScreen ? 'var(--font-size-sm)' : 'var(--font-size-base)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {adminUser.name || 'Admin'}
                </div>
                <div style={{ 
                  fontSize: 'var(--font-size-xs)', 
                  color: 'var(--text-light)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  Administrator
                </div>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default AdminSidebar;